import { useEffect } from "react";

const SITE_URL = "https://wishuponaplayhouse.com";
const DEFAULT_OG_IMAGE = `${SITE_URL}/images/hero.png`;

function upsertMeta(selector, attr, value) {
  if (typeof document === "undefined") return;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    const [, name] = selector.match(/\[(?:name|property)="([^"]+)"\]/) || [];
    if (selector.includes('property=')) {
      el.setAttribute("property", name);
    } else {
      el.setAttribute("name", name);
    }
    document.head.appendChild(el);
  }
  el.setAttribute(attr, value);
}

function upsertLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function upsertJsonLd(id, data) {
  let el = document.head.querySelector(`script[type="application/ld+json"][data-seo-id="${id}"]`);
  if (!el) {
    el = document.createElement("script");
    el.setAttribute("type", "application/ld+json");
    el.setAttribute("data-seo-id", id);
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

function removeJsonLd(id) {
  const el = document.head.querySelector(`script[type="application/ld+json"][data-seo-id="${id}"]`);
  if (el) el.remove();
}

/**
 * Per-route SEO. Updates document.title, description, canonical, OG, and
 * optional page-specific JSON-LD. Meant for client-side SPAs — modern
 * crawlers (Googlebot/Bingbot) render JS and pick these up, but keep the
 * base meta in index.html as a fallback.
 *
 * Props:
 *  - title: full <title> string (include brand)
 *  - description: 150–160 char meta description
 *  - path: route path starting with "/"; canonical = SITE_URL + path
 *  - image: absolute URL for og:image (defaults to site hero)
 *  - type: og:type (default "website")
 *  - jsonLd: optional object to inject as extra <script type="application/ld+json">
 *  - jsonLdId: required if jsonLd is passed — stable id used to update/remove on route change
 */
function Seo({
  title,
  description,
  path = "/",
  image = DEFAULT_OG_IMAGE,
  type = "website",
  jsonLd,
  jsonLdId,
}) {
  useEffect(() => {
    const canonical = `${SITE_URL}${path}`;

    if (title) document.title = title;

    if (description) {
      upsertMeta('meta[name="description"]', "content", description);
      upsertMeta('meta[property="og:description"]', "content", description);
      upsertMeta('meta[name="twitter:description"]', "content", description);
    }
    if (title) {
      upsertMeta('meta[property="og:title"]', "content", title);
      upsertMeta('meta[name="twitter:title"]', "content", title);
    }

    upsertLink("canonical", canonical);
    upsertMeta('meta[property="og:url"]', "content", canonical);
    upsertMeta('meta[property="og:type"]', "content", type);
    upsertMeta('meta[property="og:image"]', "content", image);
    upsertMeta('meta[name="twitter:image"]', "content", image);
    upsertMeta('meta[name="twitter:card"]', "content", "summary_large_image");

    if (jsonLd && jsonLdId) {
      upsertJsonLd(jsonLdId, jsonLd);
    }

    return () => {
      if (jsonLdId) removeJsonLd(jsonLdId);
    };
  }, [title, description, path, image, type, jsonLd, jsonLdId]);

  return null;
}

export default Seo;
